// Composition root of the app (index.html): the only place that knows every layer.
//
//   #hud ── mountHud(boot services) ── loading card, progress from the data layer
//   loadWorldData ─► createWorld ─► createServices ─► mountHud(real services)   leader screen now
//                                 └─► createMapView (#map, hidden) ─ mount + GPU warm-up ─► bindView
//
// The map's warm-up (tens of seconds on a busy GPU) runs behind the leader screen; if a leader is
// picked first, "Preparing the map" shows until the map is ready.
//
//   data ▶ world ▶ services ▶ ui         view ◀── bindView ──▶ services
//
// Music (src/audio) is created first, so the loading card already has its control; it follows
// the boot services, then the real ones (src/app/music.ts), and sounds after the first click.
//
// Any failure lands on the loading card ("Could not load the world: …") and is rethrown to the
// console; nothing here degrades to invented data.

import type { ReadonlySignal } from '@preact/signals';
import type { LeaderId } from '../core/schema.ts';
import { loadWorldData, createFeedClients } from '../data/index.ts';
import { loadSmallLakes } from '../data/smalllakes.ts';
import { mountHud } from '../ui/Hud.tsx';
import { FICTION_ROUTE } from '../services/fiction/api.ts';
import { createBootServices, createServices } from '../services/index.ts';
import { createMapView } from '../view/index.ts';
import { createWorld } from '../world/index.ts';
import { installAppHarness } from './app-harness.ts';
import { bindView } from './bind.ts';
import { failedLoad } from './failure.ts';
import { bindRealMusic, createAppMusic, withRealMusic } from './music.ts';
import { createFootprint, withFootprint } from './footprint.ts';
import { requestedBackend } from './harness-bridge.ts';
import { actorLabels, physicalGrid, tierOf, withSmallLakes } from './setup.ts';

const DATA = '/data';
const BUILD_LABEL = 'Raising the map';
const PREPARING_TEXT = 'Preparing the map…';
/** The visual harness opens the page with this query flag (src/app/harness-contract.ts). */
const HARNESS_FLAG = 'harness';

/** `?director=1`: the recorder steps every clock (src/app/director.ts). */
const DIRECTOR_FLAG = 'director';

const SANDBOX_WHAT = 'the sandbox';

function element(id: string): HTMLElement {
  const found = document.getElementById(id);
  if (!found) {
    throw new Error(`index.html: #${id} missing`);
  }
  return found;
}

/**
 * Hide the map while it warms up; once a leader is picked (the leader screen is gone) show a small
 * note in its place. Returns the undo.
 */
function coverMap(map: HTMLElement, leader: ReadonlySignal<LeaderId | null>): () => void {
  map.style.visibility = 'hidden';
  const note = document.createElement('div');
  note.className = 'wc-map-preparing';
  note.textContent = PREPARING_TEXT;
  Object.assign(note.style, {
    position: 'fixed', inset: '0', display: 'grid', placeItems: 'center', zIndex: '0',
    color: '#d8c79a', font: '600 15px/1 "Cinzel", serif', letterSpacing: '0.12em', pointerEvents: 'none',
  });
  const stop = leader.subscribe((id) => {
    if (id !== null && !note.isConnected) {
      document.body.appendChild(note);
    }
  });
  return () => {
    stop();
    map.style.visibility = '';
    note.remove();
  };
}

/**
 * The sandbox's chunk did not load, or its start threw before its own error card existed. Without
 * this the page would stay blank and the rejection unhandled.
 */
function showSandboxFailure(err: unknown): void {
  console.error(err);

  const boot = createBootServices();
  mountHud(element('hud'), boot.services);
  boot.setProgress(failedLoad(SANDBOX_WHAT, err));
}

/** Resolve after the browser painted, so a label set just before a long synchronous step shows. */
function painted(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

async function main(): Promise<void> {
  const harness = installAppHarness({ mode: new URLSearchParams(location.search).has(DIRECTOR_FLAG) ? 'director' : 'live' });
  const { music, control } = createAppMusic();
  const slots = { sound: control };
  const boot = createBootServices();
  const hud = element('hud');
  mountHud(hud, boot.services, slots);
  const unbindBoot = bindRealMusic(boot.services, music);

  try {
    const [data, smallLakes] = await Promise.all([loadWorldData(DATA, { onProgress: boot.setProgress }), loadSmallLakes(DATA)]);
    boot.setProgress({ done: 1, total: 1, label: BUILD_LABEL });
    await painted();

    const world = createWorld(data);
    for (const warning of world.warnings) {
      console.warn(warning);
    }

    // The leader screen needs only the world: show it now, and build the map behind it.
    const handle = createServices({ world, clients: createFeedClients() });
    const sounding = withRealMusic(handle.services, music);
    mountHud(hud, withSmallLakes(sounding, smallLakes), slots);
    unbindBoot();
    bindRealMusic(handle.services, music);

    const map = element('map');
    const uncover = coverMap(map, handle.services.state.leader);
    const view = createMapView({
      physical: physicalGrid(data.grid),
      tier: tierOf(location.search),
      backend: requestedBackend() === 'webgl2' ? 'webgl2' : 'auto',
      smallLakes,
    });
    await view.mount(map);
    view.setActors(actorLabels(world, [...data.world.actors, ...data.world.blocs]));
    view.core.engine.afterFrame(() => harness.frame());

    const services = withSmallLakes(withFootprint(sounding, createFootprint(view)), smallLakes);
    bindView(view, services);
    mountHud(hud, services, slots);
    uncover();

    // Live feeds change by the minute and the launch API answers HTTP 429 to repeated runs: a
    // harness shot must look the same without the network.
    if (!new URLSearchParams(location.search).has(HARNESS_FLAG)) {
      handle.startFeeds();
    }

    harness.attach(view, services, handle.store);
    harness.markReady();
  } catch (err) {
    console.error(err);
    boot.fail(err);
    harness.fail(err);
  }
}

// '?timeline=fiction' is the what-if sandbox: its own page code, loaded only when asked for.
const sandbox = new URLSearchParams(location.search).get(FICTION_ROUTE.param) === FICTION_ROUTE.value;

void (sandbox ? import('./fiction.ts').then((m) => m.startFiction()).catch(showSandboxFailure) : main());
